import { useState } from 'react';
import { Droplets, Heart, Trash2, NotebookPen } from 'lucide-react';
import { api, fileUrl } from '../lib/api';
import { useI18n } from '../i18n';
import { toast } from 'sonner';
import Tilt from './living/Tilt';
import LivingImage from './living/LivingImage';
import LivingEmoji from './living/LivingEmoji';
import { burst } from '../lib/fx';
import SpeciesIcon from './species/SpeciesIcon';
import { matchCatalogId } from '../lib/species';
import { PLANT_CATALOG } from '../lib/data';
import { showBridgeRewardToast } from '../lib/bridgeReward';

export default function PlantCard({ plant, onChanged, onOpen }) {
    const { t, locale } = useI18n();
    const [confirming, setConfirming] = useState(false);
    const [watering, setWatering] = useState(false);
    const [failedImage, setFailedImage] = useState(null);

    const water = async (e) => {
        e.stopPropagation();
        burst(e.currentTarget, 'water');
        try {
            const r = await api.post(`/plants/${plant.id}/water`);
            toast.success(`${plant.name} watered`);
            // Physical -> virtual bridge, "watering" trigger (see backend
            // `grant_bridge_reward`) -- best-effort, silently absent for a
            // non-Premium user or a plant already watered-and-rewarded
            // today, so no extra branching needed here beyond the shared
            // helper's own `granted` check.
            showBridgeRewardToast(r.data?.bridge_reward, t);
            onChanged?.();
        } catch {
            toast.error('Could not water plant');
        } finally {
            setWatering(false);
        }
    };

    const del = async (e) => {
        e.stopPropagation();
        if (!confirming) {
            setConfirming(true);
            toast(t('garden.confirm_delete'));
            setTimeout(() => setConfirming(false), 3000);
            return;
        }
        setConfirming(false);
        try {
            await api.delete(`/plants/${plant.id}`);
            toast.success(`${plant.name} ${t('garden.deleted_suffix')}`);
            onChanged?.();
        } catch {
            toast.error('Could not delete plant');
        }
    };

    const healthColor = plant.health > 70 ? 'bg-brand-green' : plant.health > 40 ? 'bg-brand-ochre' : 'bg-brand-terracotta';
    const displayName = plant.display_name || plant.name;
    const img = plant.image_path ? fileUrl(plant.image_path) : null;
    const catalogId = matchCatalogId(plant, PLANT_CATALOG);

    return (
        <Tilt
            data-testid={`plant-card-${plant.id}`}
            onClick={() => onOpen?.(plant)}
            max={7}
            className="group bg-brand-card border border-brand-border rounded-3xl p-5 cursor-pointer"
        >
            <div className="flex items-start gap-3">
                {img ? (
                    <LivingImage src={img} alt={plant.name} className="depth-2 w-14 h-14 shrink-0 rounded-2xl border border-brand-border" />
                ) : catalogId ? (
                    <SpeciesIcon kind="plant" id={catalogId} emoji={plant.emoji} name={plant.name} size={56} className="depth-2" />
                ) : (
                    <div className="depth-2 w-14 h-14 shrink-0 rounded-2xl bg-gradient-to-b from-brand-sage/20 to-brand-bg2 flex items-end justify-center pb-1 text-3xl">
                        <LivingEmoji seed={plant.id}>{plant.emoji || '🌱'}</LivingEmoji>
                    </div>
                )}
                <div className="depth-1 flex-1 min-w-0">
                    <h4 className="font-serif text-lg text-brand-ink truncate">{plant.name}</h4>
                    <div className="text-xs text-brand-ink3 mt-0.5">{plant.stage} · {plant.days} {plant.days === 1 ? 'day' : 'days'}</div>
                </div>
                <button
                    data-testid={`btn-delete-plant-${plant.id}`}
                    onClick={del}
                    className={`transition-colors rounded-full ${confirming ? 'bg-brand-terracotta text-white px-3 py-1 text-xs font-medium' : 'text-brand-ink3 hover:text-brand-terracotta p-1.5'}`}
                    aria-label={t('garden.delete')}
                >
                    {confirming ? t('garden.confirm_short') : <Trash2 size={16} strokeWidth={1.6} />}
                </button>
            </div>

            <div className="mt-4">
                <div className="flex items-center justify-between text-[10px] uppercase tracking-widest font-semibold text-brand-ink3 mb-1.5">
                    <span className="inline-flex items-center gap-1"><Heart size={11} strokeWidth={1.8} /> {t('garden.health')}</span>
                    <span>{Number.isFinite(plant.health) ? `${plant.health}%` : '—'}</span>
                </div>
                <div className="h-1.5 bg-brand-bg2 rounded-full overflow-hidden">
                    <div className={`bar-alive h-full rounded-full ${healthColor} transition-all duration-700`} style={{ width: `${plant.health}%` }} />
                </div>
            </div>

            <div className="mt-4 flex items-center gap-2">
                <button
                    data-testid={`btn-water-${plant.id}`}
                    onClick={water}
                    disabled={watering}
                    aria-busy={watering}
                    className="flex-1 inline-flex items-center justify-center gap-1.5 bg-brand-green text-white text-xs font-medium px-3 py-2 rounded-full hover:bg-brand-greenDark transition-colors"
                >
                    <Droplets size={13} strokeWidth={1.8} />
                    {t('garden.water')}
                </button>
                <button
                    data-testid={`btn-journal-${plant.id}`}
                    aria-label={locale === 'da' ? `Åbn journal for ${displayName}` : `Open journal for ${displayName}`}
                    onClick={(e) => { e.stopPropagation(); onOpen?.(plant, 'journal'); }}
                    className="inline-flex items-center justify-center gap-1 bg-brand-bg2 text-brand-ink text-xs px-3 py-2 rounded-full hover:bg-brand-border transition-colors"
                >
                    <NotebookPen size={13} strokeWidth={1.8} />
                </button>
            </div>
        </Tilt>
    );
}
